import type { SpaceMessage } from "@/api/spaces";
import { getMessage } from "@/api/spaces";
import type { ConversationMessage } from "@/stores/conversation-store";
import { stripCursorDisplayTokens } from "@/lib/cursor-token-display";
import { directApprovalNotice } from "@/lib/direct-approval-notice";
import { directResultNotice } from "@/lib/direct-result-notice";
import { filingReceipt } from "@/lib/filing-receipt";
import { nativeResultNotice } from "@/lib/native-result-notice";
import { nextPromptFromMetadata } from "@/lib/next-prompt-metadata";
import { steeredIntoExecutionId } from "@/lib/steered-prompt-metadata";
import { messageAttachments } from "@/lib/message-attachments";
import { nativeToolStepText, toolStepLabel } from "@/lib/native-tool-step-text";

type FetchMessage = (messageId: string) => Promise<SpaceMessage>;

export async function hydrateConversationMessages(
  apiMessages: SpaceMessage[],
  fetchMessage: FetchMessage = getMessage,
): Promise<ConversationMessage[]> {
  const fullMessages = await Promise.all(
    apiMessages.map(async (message) => {
      if (!hasTruncatedContent(message.content)) return message;
      try {
        return await fetchMessage(message.id);
      } catch {
        return message;
      }
    }),
  );

  return fullMessages
    .filter(isConversationRole)
    .map(spaceMessageToConversationMessage);
}

export function hasTruncatedContent(content: SpaceMessage["content"]): boolean {
  if (Array.isArray(content)) {
    return content.some((block) => {
      if (!isRecord(block)) return false;
      return block.truncated === true;
    });
  }
  return isRecord(content) && content.truncated === true;
}

function spaceMessageToConversationMessage(
  message: SpaceMessage,
): ConversationMessage {
  const content = messageContentToText(message.content);
  const activityKind = messageActivityKind(message);
  return {
    id: message.id,
    nativeFinal: message.metadata?.native_message_id
      ? message.metadata.native_final === true
      : undefined,
    backgroundResult: nativeResultNotice(message.metadata, content),
    directResult: directResultNotice(message.metadata, content),
    directApproval: directApprovalNotice(message.metadata),
    nativeSubagentId: message.metadata?.native_message_id
      ? (message.metadata.native_subagent_id as string)
      : undefined,
    role: message.role,
    content: stripCursorDisplayTokens(
      nativeToolStepText(message.metadata, content),
    ),
    contentLabel: toolStepLabel(message.metadata),
    attachments: messageAttachments(message.content),
    isStreaming: message.metadata?.native_message_id
      ? message.role !== "user" && message.metadata.native_final === false
      : undefined,
    activityKind,
    toolFailed: messageToolFailed(message),
    desktopReceipt:
      activityKind === "filing" ? undefined : messageReceipt(message),
    filingReceipt:
      activityKind === "filing"
        ? filingReceipt(message.metadata?.receipt)
        : undefined,
    conversationId: message.conversation || undefined,
    executionId:
      (message.metadata?.execution_id as string) ??
      message.execution ??
      undefined,
    steeredIntoExecutionId: steeredIntoExecutionId(message.metadata),
    stepId: (message.metadata?.step_id as string) ?? undefined,
    sessionId:
      ((message.metadata?.runtime_session_id ??
        message.metadata?.session_id) as string) ?? undefined,
    conversationFrameId:
      (message.metadata?.conversation_frame_id as string) ?? undefined,
    clientRequestId:
      (message.metadata?.client_request_id as string) ?? undefined,
    executionStartedAt:
      (message.metadata?.execution_started_at as string) ?? undefined,
    executionCompletedAt:
      (message.metadata?.execution_completed_at as string) ?? undefined,
    nextPrompt: nextPromptFromMetadata(message.metadata),
    createdAt: message.created_at,
  };
}

function messageActivityKind(
  message: SpaceMessage,
): ConversationMessage["activityKind"] {
  if (message.role !== "system" && !message.metadata?.native_message_id)
    return undefined;
  const kind = message.metadata?.activity_kind;
  if (
    kind === "progress" ||
    kind === "tool_call" ||
    kind === "desktop_interaction" ||
    kind === "filing"
  ) {
    return kind;
  }
  return typeof message.metadata?.tool_call === "string"
    ? "tool_call"
    : undefined;
}

function messageToolFailed(message: SpaceMessage): boolean | undefined {
  return message.metadata?.tool_failed === true ? true : undefined;
}

function messageReceipt(
  message: SpaceMessage,
): Record<string, unknown> | undefined {
  const receipt = message.metadata?.receipt;
  return isRecord(receipt) ? receipt : undefined;
}

function isConversationRole(
  message: SpaceMessage,
): message is SpaceMessage & { role: ConversationMessage["role"] } {
  return (
    message.role === "user" ||
    message.role === "assistant" ||
    message.role === "system"
  );
}

function messageContentToText(content: SpaceMessage["content"]): string {
  if (typeof content === "string") return content;
  if (isRecord(content) && typeof content.text === "string") {
    return content.text;
  }
  return JSON.stringify(content);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
